/**
 * #4504 — public game submissions: an indie developer proposes a browser game for the Web Games catalog.
 *
 * One schema serves the public form (client-side checks), the public API (the real check) and the admin
 * review queue, so the three can never disagree about what a valid submission is. Spec:
 * `_docs/Implementation/FunctionalSpecs/4504-web-game-submissions.md`.
 */
import { z } from 'zod';
import { EXTERNAL_GAME_DEVICES, EXTERNAL_GAME_INPUTS, EXTERNAL_GAME_MODES, type ExternalGameDevice, type ExternalGameInput, type ExternalGameMode } from './ExternalGames';

/** `approving` is a claim: one reviewer is creating the game. It is released if that fails, and goes stale after `APPROVAL_CLAIM_TTL_MS`. */
export const WEB_GAME_SUBMISSION_STATUSES = ['pending', 'approving', 'approved', 'rejected'] as const;
export const APPROVAL_CLAIM_TTL_MS = 5 * 60 * 1000;
export type WebGameSubmissionStatus = (typeof WEB_GAME_SUBMISSION_STATUSES)[number];

/**
 * The trap field: invisible to people, filled by bots that fill every input. Never part of a real submission.
 * Deliberately NOT a real-sounding field name ("website", "url", "company"): browsers and password managers
 * autofill those even with autocomplete off, and a filled trap silently drops a real developer's submission.
 */
export const SUBMISSION_HONEYPOT_FIELD = 'alt_ref_code';
/** A form submitted faster than a person can fill it in is a script. */
export const SUBMISSION_MIN_FILL_MS = 4000;
export const SUBMISSION_MAX_TAGS = 5;
/** The request body is refused above this before it is parsed. A real submission is well under 4 KB. */
export const SUBMISSION_MAX_BODY_BYTES = 32 * 1024;

/** Hosts a submission may not point at: our own sites are not third-party games. */
export const SUBMISSION_BLOCKED_HOSTS = ['telegames.ai', 'telegamez.com'] as const;

const DISPOSABLE_EMAIL_DOMAINS = new Set([
  'tempmail.com', 'temp-mail.org', 'guerrillamail.com', 'sharklasers.com', '10minutemail.com', 'throwaway.email',
  'mailinator.com', 'trashmail.com', 'yopmail.com', 'getnada.com', 'maildrop.cc', 'dispostable.com', 'fakeinbox.com', 'moakt.com',
]);
/** A throwaway inbox is no way to reach a developer. The list is small on purpose: it stops the lazy cases, not the determined ones. */
export const isDisposableEmail = (email: string): boolean => {
  const domain = email.trim().toLowerCase().split('@')[1] ?? '';
  return DISPOSABLE_EMAIL_DOMAINS.has(domain);
};

/**
 * Canonical form of a link we will store and compare: https only, no credentials (`https://telegames.ai@evil.example`
 * reads as one site and goes to another), no fragment (so `x.com/#a` and `x.com/` are one game), control characters
 * gone. `null` when it is not a link we accept.
 */
export function canonicalHttpsUrl(input: string): string | null {
  const cleaned = input.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  let parsed: URL;
  try { parsed = new URL(cleaned); } catch { return null; }
  if (parsed.protocol !== 'https:' || !parsed.hostname || parsed.username || parsed.password) return null;
  parsed.hash = '';
  return parsed.toString();
}

/** Form fields arrive as '' when left blank: that means "not given". */
const blankToUndefined = (value: unknown) => (typeof value === 'string' && value.trim() === '' ? undefined : value);
const optionalText = (max: number) => z.preprocess(blankToUndefined, z.string().trim().max(max).optional());
const unique = <T extends z.ZodTypeAny>(item: T, max: number) => z.array(item).max(max).refine(list => new Set(list).size === list.length, 'Remove the duplicates.');

export const webGameSubmissionSchema = z.object({
  /** The game's link. Resolved and re-validated by the server (https only). */
  url: z.string().trim().min(1, 'Add the link to your game.').max(2048, 'That link is too long.'),
  title: z.string().trim().min(1, 'Give your game a title.').max(120, 'Keep the title to 120 characters.'),
  shortDescription: z.string().trim().min(10, 'Describe the game in a sentence or two.').max(280, 'Keep this to 280 characters.'),
  creatorName: z.string().trim().min(1, 'Tell us who made it.').max(120, 'Keep the name to 120 characters.'),
  creatorHandle: optionalText(60),
  creatorUrl: z.preprocess(blankToUndefined, z.string().trim().max(2048)
    .transform((value, ctx) => {
      const canonical = canonicalHttpsUrl(value);
      if (!canonical) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Use a plain https:// link, without a username or password.' });
      return canonical ?? z.NEVER;
    }).optional()),
  mode: z.enum(EXTERNAL_GAME_MODES, { errorMap: () => ({ message: 'Pick how it is played.' }) }),
  minPlayers: z.coerce.number().int().min(1, 'At least 1 player.').max(10_000).default(1),
  maxPlayers: z.preprocess(blankToUndefined, z.coerce.number().int().min(1).max(10_000).optional()),
  inputs: unique(z.enum(EXTERNAL_GAME_INPUTS), EXTERNAL_GAME_INPUTS.length).default([]),
  devices: unique(z.enum(EXTERNAL_GAME_DEVICES), EXTERNAL_GAME_DEVICES.length).default([]),
  tags: unique(z.string().trim().toLowerCase().min(1).max(40).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Tags use letters, numbers and hyphens.'), SUBMISSION_MAX_TAGS).default([]),
  note: optionalText(1000),
  contactEmail: z.string().trim().toLowerCase().min(1, 'We need an email to reach you.').email('That does not look like an email address.').max(254),
  acceptedTerms: z.literal(true, { errorMap: () => ({ message: 'Please confirm this so we can consider your game.' }) }),
  /** The honeypot. Anything but empty marks the request as a bot. */
  [SUBMISSION_HONEYPOT_FIELD]: z.string().max(500).optional(),
  /**
   * Milliseconds the form was open, measured by the browser's own monotonic clock. NOT a timestamp: a client clock
   * that is minutes off would make a timestamp look like a too-fast submit and lose a real developer's work.
   * A submit under `SUBMISSION_MIN_FILL_MS` is asked to wait and try again; a bot can of course send any number,
   * so this only catches the lazy ones.
   */
  elapsedMs: z.number().int().min(0).max(7 * 24 * 60 * 60 * 1000).optional(),
}).strict().refine(game => game.maxPlayers === undefined || game.maxPlayers >= game.minPlayers, {
  message: 'Max players must be at least the minimum.', path: ['maxPlayers'],
});

export type WebGameSubmissionInput = z.infer<typeof webGameSubmissionSchema>;
/** What the form holds before validation: every field may be blank or a string. */
export type WebGameSubmissionFieldErrors = Partial<Record<keyof WebGameSubmissionInput | 'form', string[]>>;

/** What the admin review queue sees: everything the developer entered, plus the review state. */
export interface WebGameSubmissionSummary {
  id: string;
  status: WebGameSubmissionStatus;
  title: string;
  shortDescription: string;
  creatorName: string;
  creatorHandle: string | null;
  creatorUrl: string | null;
  url: string;
  mode: ExternalGameMode;
  minPlayers: number;
  maxPlayers: number | null;
  inputs: ExternalGameInput[];
  devices: ExternalGameDevice[];
  tags: string[];
  note: string | null;
  contactEmail: string;
  createdAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  gameId: string | null;
  /** The check run when the submission was approved; `enabled: false` there means it needs a follow-up. */
  reviewResult: { checkStatus: string | null; checkReason: string | null; enabled: boolean } | null;
}
